import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Select,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Code,
} from '@chakra-ui/react';
import { FiSend, FiRefreshCw, FiFilter } from 'react-icons/fi';
import { apiService } from '../ExternalApiService';
import { formatMMMddyyyyHHmm } from '../util/dateUtil';
import Pagination from '../components/Pagination';
import { MotionVStack, MotionGrid, MotionCard, MotionBox, variants } from '../components/motion';

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [emails, setEmails] = useState([]);
  const [emailTotal, setEmailTotal] = useState(0);
  const [emailSentCount, setEmailSentCount] = useState(0);
  const [paginationData, setPaginationData] = useState(null)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const toast = useToast();

  const fetchMessages = useCallback(async (page, limit) => {
    setIsLoading(true);
    try {
      const data = await apiService.fetchMessages(page, limit);
      setMessages(data.data.data);
      setPaginationData({
        current: data.data.current_page,
        total: data.data.total,
        perPage: data.data.per_page,
        lastPage: data.data.last_page,
        sentMessages: data.stats.sent,
        failedMessages: data.stats.failed
      })
      // Also fetch emails with the same pagination
      try {
        const emailRes = await apiService.fetchEmails(page, limit);
        // Assume similar shape { data: { data: [...], total: number } }
        const emailItems = emailRes?.data?.data ?? [];
        const totalEmails = emailRes?.data?.total ?? emailItems.length ?? 0;
        const sentEmails = emailItems.reduce((acc, e) => acc + (String(e.status).toLowerCase() === 'sent' ? 1 : 0), 0);
        setEmails(emailItems);
        setEmailTotal(Number(totalEmails) || 0);
        setEmailSentCount(Number(sentEmails) || 0);
      } catch (e) {
        // If emails fetch fails, show a toast but keep messages visible
        setEmails([]);
        setEmailTotal(0);
        setEmailSentCount(0);
        toast({
          title: 'Warning',
          description: 'Failed to fetch emails',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      toast({
        title: 'Error',
        description: 'Failed to fetch messages',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'green';
      case 'pending': return 'yellow';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };

  const getTypeColor = (type) => {
    return type === 'email' ? 'blue' : 'purple';
  };

  const filteredMessages = messages.filter((message) => {
    const matchesFilter = filter === 'all' || message.status === filter;
    const target = `${message.recipient ?? ''} ${message.template_name ?? ''}`.toLowerCase();
    const matchesSearch = target.includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const messageStats = {
    total: (paginationData?.total || 0) + (emailTotal || 0),
    sent: (paginationData?.sentMessages || 0) + (emailSentCount || 0),
    failed: paginationData?.failedMessages,
  };

  useEffect(() => {
    fetchMessages(page, limit);
  }, [page, limit, fetchMessages]);

  const openPreview = (msg) => {
    setSelectedMessage(msg);
    setIsPreviewOpen(true);
  };
  const closePreview = () => {
    setIsPreviewOpen(false);
    setSelectedMessage(null);
  };

  return (
    <MotionVStack spacing={6} align="stretch" variants={variants.container} initial="hidden" animate="show">
      <MotionBox variants={variants.item}>
        <HStack justify="space-between" align="center">
          <Heading size="lg">Messages</Heading>
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={() => fetchMessages(page, limit)}
            isLoading={isLoading}
          >
            Refresh
          </Button>
        </HStack>
        <Text color="gray.600" mt={2}>
          View and manage your sent messages
        </Text>
      </MotionBox>

      <MotionGrid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" }} gap={4} variants={variants.container}>
        <MotionCard variants={variants.item}>
          <CardBody>
            <Text fontSize="sm" color="gray.600">Total Messages</Text>
            <Text fontSize="2xl" fontWeight="bold">{messageStats.total}</Text>
          </CardBody>
        </MotionCard>
        <MotionCard variants={variants.item}>
          <CardBody>
            <Text fontSize="sm" color="gray.600">SMS Total</Text>
            <Text fontSize="2xl" fontWeight="bold">{paginationData?.total || 0}</Text>
          </CardBody>
        </MotionCard>
        <MotionCard variants={variants.item}>
          <CardBody>
            <Text fontSize="sm" color="gray.600">Email Total</Text>
            <Text fontSize="2xl" fontWeight="bold">{emailTotal || 0}</Text>
          </CardBody>
        </MotionCard>
        <MotionCard variants={variants.item}>
          <CardBody>
            <Text fontSize="sm" color="gray.600">Sent</Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.500">{messageStats.sent}</Text>
          </CardBody>
        </MotionCard>
        <MotionCard variants={variants.item}>
          <CardBody>
            <Text fontSize="sm" color="gray.600">Failed</Text>
            <Text fontSize="2xl" fontWeight="bold" color="red.500">{messageStats.failed}</Text>
          </CardBody>
        </MotionCard>
      </MotionGrid>

      <MotionCard variants={variants.item}>
        <CardBody>
          <HStack spacing={4}>
            <Input
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              flex={1}
            />
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              w="200px"
            >
              <option value="all">All Status</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </Select>
            <Select
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              w="200px"
            >
              <option value="10">10 items per page</option>
              <option value="25">25 items per page</option>
              <option value="50">50 items per page</option>
            </Select>
          </HStack>
        </CardBody>
      </MotionCard>

      <MotionCard variants={variants.item}>
        <CardHeader>
          <Heading size="md">SMS History</Heading>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Box textAlign="center" py={8}>
              <Spinner size="xl" />
            </Box>
          ) : filteredMessages.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              No messages found matching your criteria.
            </Alert>
          ) : (
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Recipient</Th>
                    <Th>Type</Th>
                    <Th>Template</Th>
                    <Th>Status</Th>
                    <Th>Sent At</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredMessages.map((message) => (
                    <Tr key={`msg-${message.id}`}>
                      <Td>{message.recipient}</Td>
                      <Td>
                        <Badge colorScheme={getTypeColor(message.type)}>
                          {message.type.toUpperCase()}
                        </Badge>
                      </Td>
                      <Td>{message.template_name}</Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(message.status)}>
                          {message.status}
                        </Badge>
                      </Td>
                      <Td>{formatMMMddyyyyHHmm(message.created_at)}</Td>
                      <Td>
                        <Button size="sm" variant="ghost" onClick={() => openPreview(message)}>
                          View
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              <Pagination
                currentPage={page}
                pageSize={limit}
                totalItems={paginationData?.total ?? 0}
                onPageChange={(p) => setPage(p)}
                onPageSizeChange={(size) => { setPage(1); setLimit(size); }}
                pageSizeOptions={[10, 25, 50]}
                isLoading={isLoading}
              />
            </TableContainer>
          )}
        </CardBody>
      </MotionCard>

      <MotionCard variants={variants.item}>
        <CardHeader>
          <Heading size="md">Email History</Heading>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Box textAlign="center" py={8}>
              <Spinner size="xl" />
            </Box>
          ) : emails.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              No emails found.
            </Alert>
          ) : (
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Recipient</Th>
                    <Th>Type</Th>
                    <Th>Template</Th>
                    <Th>Status</Th>
                    <Th>Sent At</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {emails.map((email) => (
                    <Tr key={`email-${email.id}`}>
                      <Td>{email.recipient}</Td>
                      <Td>
                        <Badge colorScheme={getTypeColor('email')}>
                          EMAIL
                        </Badge>
                      </Td>
                      <Td>{email.template_name}</Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(email.status)}>
                          {email.status}
                        </Badge>
                      </Td>
                      <Td>{formatMMMddyyyyHHmm(email.created_at)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </CardBody>
      </MotionCard>

      <Modal isOpen={isPreviewOpen} onClose={closePreview} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack justify="space-between">
              <Heading size="md">Message Preview</Heading>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedMessage && (
              <VStack align="stretch" spacing={3}>
                <HStack>
                  <Text fontWeight="medium" color="gray.600">Template:</Text>
                  <Code>{selectedMessage.template_name || '-'}</Code>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" color="gray.600">Recipient:</Text>
                  <Code>{selectedMessage.recipient || '-'}</Code>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" color="gray.600">Type:</Text>
                  <Badge colorScheme={getTypeColor(selectedMessage.type)}>
                    {selectedMessage.type?.toUpperCase?.() || '-'}
                  </Badge>
                </HStack>
                <HStack>
                  <Text fontWeight="medium" color="gray.600">Status:</Text>
                  <Badge colorScheme={getStatusColor(selectedMessage.status)}>
                    {selectedMessage.status}
                  </Badge>
                </HStack>

                <Box mt={2}>
                  <Text fontWeight="medium" mb={2}>Content</Text>
                  {/* Preserve line breaks & spacing */}
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    bg="gray.50"
                    fontFamily="mono"
                    whiteSpace="pre-wrap"
                  >
                    {selectedMessage.content || '(No content)'}
                  </Box>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closePreview}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MotionVStack>
  );
};

export default Messages;
